import PermissionLink from '../../components/PermissionLink';
import { useCategoryText } from '../../utils/categoryText';
import type { MaterialRef, MaterialSource, Satisfaction } from '../../api/assets';
import React, { useState } from 'react';
import { thumbUrl, THUMB_W_SM } from '../../utils/imgUrl';
import { sortByDir, SortDir } from '../../utils/sortDesc';

// 数据资产各中心页共用的小组件: 统计卡 / CSS 条形图 / 质量与评价徽标 / 同步状态条.

export function StatCard({
    label,
    value,
    sub,
    accent,
}: {
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
    accent?: string;
}) {
    return (
        <div className="card p-4">
            <div className="text-xs text-slate-500">{label}</div>
            <div
                className={`text-2xl font-bold mt-1 ${accent || 'text-slate-800'}`}
            >
                {value}
            </div>
            {sub && (
                <div className="text-[11px] text-slate-400 mt-1">{sub}</div>
            )}
        </div>
    );
}

// BarList 横向条形图 (纯 CSS, 对齐 console dash/BarList): 行级可指定颜色/占比/下钻.
// 默认按数值倒序 (先排序再截 maxRows, Top N 才是真的 Top), 标题栏右侧可切 低→高 / 高→低;
// 有固定语义顺序的档位 (效果三档 / 采纳三档) 传 ordered, 默认原顺序.
export interface BarRow {
    label: string;
    value: number;
    /** 行条颜色, 缺省 indigo */
    color?: string;
    /** 占比列 (如 '62%'), 任一行有则整卡留位 */
    share?: string;
    /** 标签片 (如「材料来源」徽标), 跟在行名右边; label 仍是 key 与 title */
    tag?: React.ReactNode;
    /** 下钻回调 (hover 出 ›) */
    onClick?: () => void;
    /** 行名的悬停提示, 缺省就是 label (如失败原因悬停看原始错误信息) */
    title?: string;
}

export function BarList({
    title,
    hint,
    note,
    rows,
    labelWidth = 'w-24',
    maxRows = 12,
    footer,
    ordered = false,
}: {
    title: string;
    hint?: React.ReactNode;
    note?: React.ReactNode;
    rows: BarRow[];
    labelWidth?: string;
    maxRows?: number;
    footer?: React.ReactNode;
    /** true = 默认保持传入顺序 (档位类, 开关多一个「原顺序」); 缺省按数值从大到小 */
    ordered?: boolean;
}) {
    const [dir, setDir] = useState<SortDir>(ordered ? 'none' : 'desc');
    const shown = sortByDir(rows, (r) => r.value, dir).slice(0, maxRows);
    const max = Math.max(1, ...shown.map((r) => r.value));
    const hasShare = shown.some((r) => r.share);
    return (
        <div className="card p-4">
            <div className="flex items-baseline gap-2 mb-3">
                <div className="text-sm font-semibold text-slate-700">
                    {title}
                </div>
                {hint && (
                    <div className="text-[11px] text-slate-400 ml-auto">
                        {hint}
                    </div>
                )}
                {rows.length > 1 && (
                    <SortDirToggle value={dir} onChange={setDir} withOriginal={ordered}
                        className={hint ? '' : 'ml-auto'} />
                )}
            </div>
            {note && (
                <div className="text-[11px] text-slate-500 mb-2">{note}</div>
            )}
            {shown.length === 0 ? (
                <div className="text-xs text-slate-400">暂无数据</div>
            ) : (
                <div className="space-y-2">
                    {shown.map((r) => (
                        <div
                            key={r.label}
                            className={`group flex items-center gap-2 text-xs ${r.onClick ? 'cursor-pointer rounded hover:bg-slate-50' : ''}`}
                            onClick={r.onClick}
                            title={
                                r.onClick
                                    ? '点一下 → 跳到明细, 只看这一批'
                                    : undefined
                            }
                        >
                            <div
                                className={`${labelWidth} shrink-0 flex items-center gap-1.5 min-w-0`}
                                title={r.title ?? r.label}
                            >
                                <span className="truncate text-slate-600">
                                    {r.label}
                                </span>
                                {r.tag}
                            </div>
                            <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                                <div
                                    className="h-full rounded"
                                    style={{
                                        width: `${(r.value / max) * 100}%`,
                                        background:
                                            r.color || 'rgba(129,140,248,.8)',
                                        minWidth: '2%',
                                    }}
                                />
                            </div>
                            <div className="w-14 text-right text-slate-500 tabular-nums font-mono">
                                {r.value.toLocaleString()}
                            </div>
                            {hasShare && (
                                <div className="w-9 text-right text-[10px] text-slate-400">
                                    {r.share || ''}
                                </div>
                            )}
                            {r.onClick && (
                                <span className="w-2 text-slate-300 opacity-0 group-hover:opacity-100">
                                    ›
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {footer && <div className="mt-2">{footer}</div>}
        </div>
    );
}

// StackBar 多段占比堆叠条 (设备×质量等).
export function StackBar({
    segments,
    total,
}: {
    segments: { value: number; color: string; label: string }[];
    total: number;
}) {
    if (total <= 0) return <div className="h-4 bg-slate-100 rounded" />;
    return (
        <div className="flex h-4 rounded overflow-hidden bg-slate-100">
            {segments
                .filter((s) => s.value > 0)
                .map((s) => (
                    <div
                        key={s.label}
                        title={`${s.label}: ${s.value}`}
                        style={{
                            width: `${(s.value / total) * 100}%`,
                            background: s.color,
                        }}
                    />
                ))}
        </div>
    );
}

// 效果质量档位 —— **两套口径并存** (prism 迁移 0048 起同一列; 后端 material-library assetprism/quality.go):
//   v1 现象词表  best / good / acceptable / poor               配 score 0–100 + defects[] 缺陷码
//   v2 加工质量评估  perfect / minor_flaw / abnormal / unjudgable   配 indicators[] 核心指标 + unable_reason
// 历史行是 v1, 新打的是 v2; 一行是哪套只看 quality 落在哪个集合 (别靠 score 是否为空).
// 口径常量与归并函数在 quality.ts (纯数据, 不放在组件文件里以免 react-refresh 告警); 这里只留徽标用的文案与配色.
export const QUALITY_LABEL: Record<string, string> = {
    best: '完美',
    good: '良好',
    acceptable: '可接受',
    poor: '不达标',
    perfect: '效果完美',
    minor_flaw: '轻微瑕疵',
    abnormal: '异常',
    unjudgable: '无法判断',
};
export const QUALITY_COLOR: Record<string, string> = {
    best: '#10b981',
    good: '#84cc16',
    acceptable: '#f59e0b',
    poor: '#ef4444',
    perfect: '#10b981',
    minor_flaw: '#f59e0b',
    abnormal: '#ef4444',
    unjudgable: '#94a3b8',
};
export function QualityBadge({
    quality,
    source,
}: {
    quality: string;
    source?: string;
}) {
    if (!quality) {
        return (
            <span className="inline-block px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-400 text-[11px] whitespace-nowrap">
                未评级
            </span>
        );
    }
    const cls: Record<string, string> = {
        best: 'bg-emerald-50 text-emerald-600 border-emerald-200',
        good: 'bg-lime-50 text-lime-600 border-lime-200',
        acceptable: 'bg-amber-50 text-amber-600 border-amber-200',
        poor: 'bg-rose-50 text-rose-600 border-rose-200',
        perfect: 'bg-emerald-50 text-emerald-600 border-emerald-200',
        minor_flaw: 'bg-amber-50 text-amber-600 border-amber-200',
        abnormal: 'bg-rose-50 text-rose-600 border-rose-200',
        unjudgable: 'bg-slate-100 text-slate-500 border-slate-200',
    };
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full border text-[11px] whitespace-nowrap ${cls[quality] || 'bg-slate-50 text-slate-500 border-slate-200'}`}
        >
            {QUALITY_LABEL[quality] || quality}
            {source === 'manual' ? ' ·人工' : ''}
        </span>
    );
}

// 用户评价徽标 (耗材识别中心 good/bad/none).
export function RatingBadge({ rating }: { rating: string }) {
    const map: Record<string, { text: string; cls: string }> = {
        good: {
            text: '👍 好评',
            cls: 'bg-emerald-50 text-emerald-600 border-emerald-200',
        },
        bad: {
            text: '👎 差评',
            cls: 'bg-rose-50 text-rose-600 border-rose-200',
        },
    };
    const v = map[rating] || {
        text: '无反馈',
        cls: 'bg-slate-50 text-slate-400 border-slate-200',
    };
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full border text-[11px] whitespace-nowrap ${v.cls}`}
        >
            {v.text}
        </span>
    );
}

// 参数采纳徽标 (adopted=原样采纳 / adjusted=有调整).
export function VerdictBadge({ verdict }: { verdict: string }) {
    const map: Record<string, { text: string; cls: string }> = {
        adopted: {
            text: '原样采纳',
            cls: 'bg-brand-50 text-brand-600 border-brand-200',
        },
        adjusted: {
            text: '有调整',
            cls: 'bg-amber-50 text-amber-600 border-amber-200',
        },
        no_recommendation: {
            text: '无推荐参数',
            cls: 'bg-slate-50 text-slate-500 border-slate-200',
        },
    };
    const v = map[verdict] || {
        text: verdict || '—',
        cls: 'bg-slate-50 text-slate-500 border-slate-200',
    };
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full border text-[11px] whitespace-nowrap ${v.cls}`}
        >
            {v.text}
        </span>
    );
}

// DataSourceBar 直读型数据源的状态条 (直连源库实时查询, 无同步、无副本).
// source 指明是哪个源库 (耗材识别/加工数据是 prism, c10s 商家数据中心是 customthings-service),
// 缺省沿用 prism.
export function DataSourceBar({
    health,
    source = 'prism 源库',
}: {
    health: { enabled: boolean; ok?: boolean; error?: string } | null;
    source?: string;
}) {
    return (
        // 源库名带括号后容易折行 (c10s 那条 "…(customthings-service)"), 两行都禁止换行.
        <div className="text-xs text-slate-500 text-right leading-5 whitespace-nowrap shrink-0">
            {health && health.enabled && health.ok === false ? (
                <div className="text-rose-600" title={health.error}>
                    ⚠ 数据源不可用
                </div>
            ) : (
                <div>数据实时来自 {source}</div>
            )}
            <div className="text-[11px] text-slate-400">
                直连查询 · 不落库 · 每次打开即最新
            </div>
        </div>
    );
}

// 加工缺陷 code → 中文 (未收录原样显示).
// SSOT 是 prism 的效果标注词表 (domain/prompts/effect_label_prompts.py 的
// CUTTING_/ENGRAVING_/COMMON_DEFECTS, 13 码全表见 prism 文档 docs/api/dashboard-api.md),
// prism 模型词表演进后这里必须跟抄 —— 2026-10-29 库里已产出 burn_marks / edge_blur /
// surface_damage / dross 而旧表未收录, 且旧表 warped / yellowed 是不存在的死键
// (真实码是 warping; 「发黄」已并入 burn_marks 烧焦发黄), 导致缺陷下拉显示英文码.
export const DEFECT_LABEL: Record<string, string> = {
    // 切割族 (craft 含 CUTTING)
    not_cut_through: '未切透',
    excessive_kerf: '切口过宽',
    charred_edge: '边缘烧焦',
    dross: '挂渣残留',
    warping: '变形翘曲',
    // 雕刻族 (craft 含 ENGRAVING / PRINT)
    too_shallow: '雕刻过浅',
    too_deep: '雕刻过深',
    burn_marks: '烧焦发黄',
    uneven: '深浅不均',
    low_contrast: '对比度不足',
    edge_blur: '边缘模糊',
    // 通用 (两族都含)
    misalignment: '位置偏移',
    surface_damage: '表面损伤',
};
export const defectText = (c: string) => DEFECT_LABEL[c] || c;

// 不满意原因码 (prism material_archive_ratings.reasons). 词表归 Studio 客户端、按工艺分套
// 只增不改; 下表是 2026-10-08 线上实际出现过的全部 code, 新 code 原样显示.
const SATISFY_REASON_LABEL: Record<string, string> = {
    Too_shallow_engraving: '雕刻太浅',
    Too_deep_engraving: '雕刻太深',
    Not_fully_cut_through: '没切透',
    Yellowing: '发黄',
    Burnt_edges: '边缘烧焦',
};
export const satisfyReasonText = (c: string) => SATISFY_REASON_LABEL[c] || c;

/** 满意度徽标. null = 用户没评价, 显示「未评价」与"不满意"区分. */
export function SatisfactionBadge({ value }: { value?: Satisfaction | null }) {
    if (!value) return <span className="text-[11px]" style={{ color: '#cbd5e1' }}>未评价</span>;
    const cls = value.satisfied
        ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
        : 'bg-rose-50 text-rose-600 border-rose-200';
    // 列表里只放徽标, 原因与备注挂 title, 完整内容看详情.
    const tip = value.satisfied ? undefined
        : [value.reasons.map(satisfyReasonText).join(' / '), value.note].filter(Boolean).join(' · ') || undefined;
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] whitespace-nowrap ${cls}`} title={tip}>
            {value.satisfied ? '满意' : '不满意'}
        </span>
    );
}

// SortDirToggle 图表/排行的排序开关 (低→高 / 高→低, 档位类图表多一个「原顺序」), 样式同成功率卡的开关.
export function SortDirToggle({ value, onChange, withOriginal = false, className = '' }: {
    value: SortDir;
    onChange: (v: SortDir) => void;
    withOriginal?: boolean;
    className?: string;
}) {
    const options = [
        ...(withOriginal ? [{ value: 'none', label: '原顺序' }] : []),
        { value: 'asc', label: '低→高' },
        { value: 'desc', label: '高→低' },
    ];
    return (
        <div className={`shrink-0 self-center ${className}`}>
            <SegGroup value={value} options={options} onChange={(v) => onChange(v as SortDir)} />
        </div>
    );
}

// DateRangeInput 日期区间 (起 ~ 止) + 可选快捷档 (近 N 天 / 全部). 日期按 UTC 自然日 (与后端 occurred_date 同口径).
const DAY_MS = 86_400_000;
const DEFAULT_PRESETS = [7, 30, 90];

function utcDay(offsetDays: number): string {
    return new Date(Date.now() - offsetDays * DAY_MS).toISOString().slice(0, 10);
}

export function DateRangeInput({ from, to, onChange, presets = false, className = '' }: {
    from: string;
    to: string;
    onChange: (from: string, to: string) => void;
    /** 显示「近 7 / 30 / 90 天 · 全部」快捷档 */
    presets?: boolean;
    className?: string;
}) {
    const today = utcDay(0);
    const active = DEFAULT_PRESETS.find((n) => to === today && from === utcDay(n - 1));
    return (
        <div className={`flex items-center gap-1.5 min-w-0 ${className}`}>
            {presets && (
                <SegGroup value={!from && !to ? 'all' : active ? String(active) : ''}
                    options={[...DEFAULT_PRESETS.map((n) => ({ value: String(n), label: `近 ${n} 天` })), { value: 'all', label: '全部' }]}
                    onChange={(v) => (v === 'all' ? onChange('', '') : onChange(utcDay(Number(v) - 1), today))} />
            )}
            <input type="date" className="inp min-w-0 flex-1" value={from} title="开始日期 (UTC)"
                onChange={(e) => onChange(e.target.value, to)} />
            <span className="text-slate-300 shrink-0">~</span>
            <input type="date" className="inp min-w-0 flex-1" value={to} title="结束日期 (UTC, 含当天)"
                onChange={(e) => onChange(from, e.target.value)} />
        </div>
    );
}

// DislikeRateCell 不满意率单元格: 分母只算带反馈的 (满意 + 不满意), 没反馈显示「—」; 20%/8% 两道线着色.
export function DislikeRateCell({ like, dislike }: { like: number; dislike: number }) {
    const fb = like + dislike;
    if (fb === 0) return <span className="text-slate-300">—</span>;
    const rate = (dislike / fb) * 100;
    return (
        <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${rate >= 20 ? 'bg-rose-50 text-rose-600' : rate >= 8 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
            {rate.toFixed(0)}%<span className="font-normal text-slate-400 ml-1">n={fb}</span>
        </span>
    );
}

// SegGroup 分段筛选 (对齐 console dash/SegGroup): label + 一排小按钮.
// label 可省 (成功率卡的排序开关只要按钮, 前面已有卡片标题, 再加前缀文案是重复).
export function SegGroup({
    label,
    options,
    value,
    onChange,
    disabled,
    title,
}: {
    label?: string;
    options: { value: string; label: string }[];
    value: string;
    onChange: (v: string) => void;
    disabled?: boolean;
    title?: string;
}) {
    return (
        <div
            className={`flex items-center gap-2 ${disabled ? 'opacity-45' : ''}`}
            title={title}
        >
            {label && <span className="text-xs text-slate-400">{label}</span>}
            <div className="flex gap-1">
                {options.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        disabled={disabled}
                        className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                            value === o.value
                                ? 'bg-slate-900 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        } ${disabled ? 'cursor-not-allowed' : ''}`}
                        onClick={() => onChange(o.value)}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// SortTimeHeader 表头「时间」排序开关 (2026-10-21): 默认倒序 (新→旧), 点一下切正序; 箭头标当前方向.
export function SortTimeHeader({
    label,
    order,
    onToggle,
}: {
    label: string;
    order: 'asc' | 'desc';
    onToggle: () => void;
}) {
    return (
        <button
            type="button"
            className="inline-flex items-center gap-1 uppercase hover:text-slate-700"
            style={{ letterSpacing: 'inherit' }}
            title={order === 'asc' ? '当前旧→新, 点一下改为新→旧' : '当前新→旧, 点一下改为旧→新'}
            onClick={onToggle}
        >
            {label}
            <span className="text-indigo-500">{order === 'asc' ? '↑' : '↓'}</span>
        </button>
    );
}

// PendingCard 「待后端数据」灰底虚线占位卡 (对齐 console dash/PendingCard).
export function PendingCard({
    title,
    hint,
    reason,
}: {
    title: string;
    hint?: string;
    reason: string;
}) {
    return (
        <div
            className="rounded-2xl p-4"
            style={{ background: '#f8fafc', border: '1px dashed #cbd5e1' }}
        >
            <div className="flex items-baseline gap-2 mb-2">
                <div className="text-sm font-semibold text-slate-600">
                    {title}
                </div>
                {hint && (
                    <div className="text-[11px] text-slate-400">{hint}</div>
                )}
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">
                    待后端数据
                </span>
            </div>
            <div className="text-[11px] text-slate-400 leading-relaxed">
                {reason}
            </div>
        </div>
    );
}

// 效果三档归并 (与 console effect-state.ts 一致): 完美(best+perfect)/轻微瑕疵(good+minor_flaw)/不达标(acceptable+poor+abnormal);
// v2 的 unjudgable 单列一档, 不进任何比例的分母.
export const TIER3 = {
    perfect: {
        label: '效果完美',
        short: '完美',
        bar: '#34d399',
        badge: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    },
    minor: {
        label: '效果存在轻微瑕疵',
        short: '轻微瑕疵',
        bar: '#fbbf24',
        badge: 'bg-amber-50 text-amber-600 border-amber-200',
    },
    fail: {
        label: '效果不达标（有明显瑕疵）',
        short: '不达标',
        bar: '#fb7185',
        badge: 'bg-rose-50 text-rose-600 border-rose-200',
    },
    unjudgable: {
        label: '无法判断（图看不出来）',
        short: '无法判断',
        bar: '#94a3b8',
        badge: 'bg-slate-100 text-slate-500 border-slate-200',
    },
};

export function tier3Of(quality: string): keyof typeof TIER3 | '' {
    if (quality === 'best' || quality === 'perfect') return 'perfect';
    if (quality === 'good' || quality === 'minor_flaw') return 'minor';
    if (quality === 'acceptable' || quality === 'poor' || quality === 'abnormal') return 'fail';
    if (quality === 'unjudgable') return 'unjudgable';
    return '';
}

// EffectBadge 效果徽标: three=加工页三档措辞 / four=识别页逐次加工四档措辞.
const FOUR_LABEL: Record<string, string> = {
    best: '可交付',
    good: '良好',
    acceptable: '可用',
    poor: '需重做',
    perfect: '可交付',
    minor_flaw: '轻微瑕疵',
    abnormal: '需重做',
    unjudgable: '无法判断',
};

export function EffectBadge({
    quality,
    source,
    wording = 'three',
    score,
}: {
    quality: string;
    source?: string;
    wording?: 'three' | 'four';
    score?: number;
}) {
    if (!quality) {
        return (
            <span className="inline-block px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-400 text-[11px] whitespace-nowrap">
                待打标
            </span>
        );
    }
    let text: string;
    let cls: string;
    if (wording === 'four') {
        text = FOUR_LABEL[quality] || quality;
        cls =
            {
                best: 'bg-emerald-50 text-emerald-600 border-emerald-200',
                good: 'bg-lime-50 text-lime-600 border-lime-200',
                acceptable: 'bg-amber-50 text-amber-600 border-amber-200',
                poor: 'bg-rose-50 text-rose-600 border-rose-200',
                perfect: 'bg-emerald-50 text-emerald-600 border-emerald-200',
                minor_flaw: 'bg-amber-50 text-amber-600 border-amber-200',
                abnormal: 'bg-rose-50 text-rose-600 border-rose-200',
                unjudgable: 'bg-slate-100 text-slate-500 border-slate-200',
            }[quality] || 'bg-slate-50 text-slate-500 border-slate-200';
    } else {
        const t = tier3Of(quality);
        text = t ? TIER3[t].short : quality;
        cls = t
            ? TIER3[t].badge
            : 'bg-slate-50 text-slate-500 border-slate-200';
    }
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full border text-[11px] whitespace-nowrap ${cls}`}
        >
            {text}
            {score != null && quality ? ` ${score}分` : ''}
            {source === 'manual' ? ' ·人工' : ''}
        </span>
    );
}

// 参数采纳双措辞: recognition=识别页(原样采纳/改了参数/无推荐) / v44=加工页(推荐参数/调整了推荐参数/无推荐可比).
export function AdoptBadge({
    verdict,
    wording = 'v44',
}: {
    verdict: string;
    wording?: 'recognition' | 'v44';
}) {
    const words: Record<
        string,
        Record<string, { text: string; cls: string }>
    > = {
        recognition: {
            adopted: {
                text: '原样采纳',
                cls: 'bg-brand-50 text-brand-600 border-brand-200',
            },
            adjusted: {
                text: '改了参数',
                cls: 'bg-amber-50 text-amber-600 border-amber-200',
            },
            no_recommendation: {
                text: '无推荐',
                cls: 'bg-slate-50 text-slate-500 border-slate-200',
            },
        },
        v44: {
            adopted: {
                text: '推荐参数',
                cls: 'bg-brand-50 text-brand-600 border-brand-200',
            },
            adjusted: {
                text: '调整了推荐参数',
                cls: 'bg-amber-50 text-amber-600 border-amber-200',
            },
            // 原型这一档叫「用户自定义参数」, 但 verdict=no_recommendation 的真实语义是
            // **没有推荐参数可比**: 实测 1,366/1,408 条落在这里, 绝大多数是直选官方材料的加工 ——
            // 它们在平台参数库里明明有推荐值, 只是判定还没接进来 (见 加工成功率口径分析-20260909).
            // 标成"用户自定义参数"等于把"判不出"说成"用户自己调的", 所以改用中性措辞.
            no_recommendation: {
                text: '无推荐可比',
                cls: 'bg-stone-100 text-stone-500 border-stone-200',
            },
        },
    };
    const v = words[wording][verdict] || {
        text: verdict || '—',
        cls: 'bg-slate-50 text-slate-500 border-slate-200',
    };
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full border text-[11px] whitespace-nowrap ${v.cls}`}
        >
            {v.text}
        </span>
    );
}

// ---------- 设备配色 (分类色板) ----------
//
// 各设备成功率按**设备**上色, 不按名次也不按数值高低:
//   - 按数值上色试过 —— 三档阈值 (<80 红 / <90 琥珀 / 其余绿) 下这份数据的各设备成功率
//     全落进 "<80%" 同一档, 整张图一片红, 颜色不承载任何信息;
//   - 颜色跟着设备走, 同一台设备恒为同色, 换排序/换筛选都不变色, 颜色本身开始表达"哪台设备".
// 色板取 dataviz 规范的分类色板 (八色定序, 顺序本身是色盲安全的机制, 不可重排/循环),
// 已过校验: 亮度带/彩度下限/相邻对 CVD ΔE 9.1 (protan) / 常视 ΔE 19.6 全 PASS;
// 其中 3 个色对白底不足 3:1, 靠每行的设备名 + 百分比 + n 直接标注兜底 (规范的 relief 规则).
// 与 fix/prism-html 分支的 prismKit.deviceColorMap 同一套色值, 后续合并时以那份为准.
const DEVICE_HUES = [
    '#2a78d6',
    '#eb6834',
    '#1baf7a',
    '#eda100',
    '#e87ba4',
    '#008300',
    '#4a3aa7',
    '#e34948',
];
// 第 9 台起并入中性灰 (规范: 超出色板不得循环生成新色). 排在后面的都是加工量极少的设备.
const DEVICE_REST = '#9096a6';

/**
 * deviceColorMap 设备 → 颜色的固定映射.
 * order 传**全局固定的设备顺序** (取加工量降序, 与当前排序/筛选无关), 保证同一设备恒为同色.
 */
export function deviceColorMap(order: string[]): Record<string, string> {
    const map: Record<string, string> = {};
    order.forEach((d, i) => {
        map[d] = i < DEVICE_HUES.length ? DEVICE_HUES[i] : DEVICE_REST;
    });
    return map;
}

// lowSampleN 判定条数低于它就不足以代表设备水平 (P2 只有 1 条判定却画成 0%, 会被当成"最差").
// 这类行整行调淡并标「样本少」, 无论正序倒序都沉到最后, 不跟正常样本抢视觉.
const lowSampleN = 5;

// SuccessRateBars 各设备加工成功率. 排序可切 (低→高 / 高→低), 配色按设备而非数值.
export function SuccessRateBars({
    rows,
    desc,
    colorMap,
}: {
    rows: { label: string; rate: number; n: number }[];
    /** true = 高→低, 默认低→高 */
    desc?: boolean;
    /** 设备 → 颜色 (deviceColorMap 产出); 不传则整列用色板首色 */
    colorMap?: Record<string, string>;
}) {
    if (rows.length === 0)
        return <div className="text-xs text-slate-400">暂无判定数据</div>;
    const sorted = [...rows].sort((a, b) => {
        // 样本少的先沉底, 再按选定方向排 —— 否则 n=1 的 0% 会顶在"最差"位置误导判断.
        const la = a.n < lowSampleN ? 1 : 0;
        const lb = b.n < lowSampleN ? 1 : 0;
        if (la !== lb) return la - lb;
        return desc ? b.rate - a.rate : a.rate - b.rate;
    });
    return (
        <div className="space-y-2">
            {sorted.map((r) => {
                const weak = r.n < lowSampleN;
                return (
                    <div
                        key={r.label}
                        className={`flex items-center gap-2 text-xs ${weak ? 'opacity-45' : ''}`}
                        title={
                            weak
                                ? `只有 ${r.n} 条已判定加工, 样本太少, 这个百分比不代表设备水平`
                                : undefined
                        }
                    >
                        <div
                            className="w-24 truncate text-slate-600 shrink-0"
                            title={r.label}
                        >
                            {r.label}
                        </div>
                        <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                            <div
                                className="h-full rounded"
                                style={{
                                    width: `${Math.max(r.rate, 1.5)}%`,
                                    background: weak
                                        ? DEVICE_REST
                                        : colorMap?.[r.label] || DEVICE_HUES[0],
                                }}
                            />
                        </div>
                        <div className="w-11 text-right font-semibold text-slate-700 tabular-nums">
                            {r.rate}%
                        </div>
                        <div className="w-20 text-right text-[10px] text-slate-400">
                            n {r.n}
                            {weak ? ' · 样本少' : ''}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// FunnelBand 转化漏斗 (级间「通过 x.x% 流失 N」; 级数由调用方给).
export function FunnelBand({
    levels,
    footer,
}: {
    levels: {
        label: string;
        value: number;
        note?: string;
        grey?: boolean;
        onClick?: () => void;
    }[];
    footer?: React.ReactNode;
}) {
    const max = Math.max(1, ...levels.map((l) => l.value));
    return (
        <div className="space-y-1">
            {levels.map((l, i) => {
                const prev = i > 0 ? levels[i - 1] : null;
                const pass =
                    prev && prev.value > 0 && !l.grey && !prev.grey
                        ? ((l.value / prev.value) * 100).toFixed(1)
                        : null;
                return (
                    <React.Fragment key={l.label}>
                        {i > 0 && (
                            <div className="pl-[150px] text-[11px] text-slate-400">
                                {pass != null ? (
                                    <>
                                        通过 {pass}% · 流失{' '}
                                        {(
                                            prev!.value - l.value
                                        ).toLocaleString()}
                                    </>
                                ) : (
                                    '口径不同，不算转化率'
                                )}
                            </div>
                        )}
                        <div
                            className={`grid items-center gap-2 text-xs ${l.onClick ? 'cursor-pointer rounded hover:bg-[#f5f6fb]' : ''}`}
                            style={{ gridTemplateColumns: '150px 1fr 96px' }}
                            onClick={l.onClick}
                            title={
                                l.onClick
                                    ? '点一下 → 跳到识别明细，只看这一级'
                                    : l.grey
                                      ? '这一级口径为全库，不随筛选变'
                                      : undefined
                            }
                        >
                            <div className="text-slate-600 truncate">
                                {l.label}
                                {l.note && (
                                    <span className="text-[10px] text-slate-400 ml-1">
                                        {l.note}
                                    </span>
                                )}
                            </div>
                            <div className="h-[26px] bg-slate-100 rounded-[7px] overflow-hidden">
                                <div
                                    className="h-full rounded-[7px]"
                                    style={{
                                        width: `${(l.value / max) * 100}%`,
                                        minWidth: 2,
                                        background: l.grey
                                            ? 'repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 6px,#f1f5f9 6px,#f1f5f9 12px)'
                                            : 'linear-gradient(90deg,#6366f1,#818cf8)',
                                    }}
                                />
                            </div>
                            <div className="text-right text-slate-700 font-semibold tabular-nums">
                                {l.value.toLocaleString()}
                                {l.onClick && (
                                    <span className="text-slate-300 ml-1">
                                        ›
                                    </span>
                                )}
                            </div>
                        </div>
                    </React.Fragment>
                );
            })}
            {footer}
        </div>
    );
}

// NoticeBar 蓝色口径说明条 (ⓘ 前缀).
export function NoticeBar({ children }: { children: React.ReactNode }) {
    return (
        <div
            className="rounded-xl px-4 py-3 text-xs leading-relaxed"
            style={{
                background: '#eef2ff',
                border: '1px solid #c7d2fe',
                color: '#3730a3',
            }}
        >
            ⓘ {children}
        </div>
    );
}

// ---------- 无材料标识的哨兵值 ----------
// 源库 material_process_archives.material_id 用 "" 与 "0" 两个值表达"这次上报没带材料标识"
// (后端 assetprism/sqlcommon.go 的 sentinelMaterialIDs, 与 prism _SENTINEL_MATERIAL_IDS 一致).
// "0" 是非空字符串, 直接 `name || id` 兜底会把它当成一个叫 0 的材料显示出来.

export function isSentinelMaterialID(id?: string): boolean {
    return !id || id === '0';
}

/** 材料名展示: 有名用名, 没名退材料标识, 哨兵行说明"没带标识"而不是显示 0. */
export function materialDisplayName(name?: string, id?: string): string {
    if (name) return name;
    return isSentinelMaterialID(id) ? '无材料标识' : (id as string);
}

// ---------- 溯源: 关联到的本平台材料 ----------
// 三个中心的详情页共用 (加工明细 / 识别明细 / UGC): 展示源侧材料线索 + 解析到的平台材料,
// 能对上就给到材料详情的跳转. 解析口径见后端 internal/pkg/matref.
export const MATERIAL_SOURCE_LABEL: Record<
    MaterialSource,
    { text: string; cls: string }
> = {
    official: { text: '官方材料', cls: 'bg-emerald-50 text-emerald-600' },
    user_custom: { text: '用户自定义', cls: 'bg-amber-50 text-amber-600' },
    unknown: { text: '无材料标识', cls: 'bg-slate-100 text-slate-500' },
};

export function MaterialSourceBadge({ source }: { source?: MaterialSource }) {
    const m = MATERIAL_SOURCE_LABEL[source || 'unknown'];
    return (
        <span
            className={`inline-block px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap ${m.cls}`}
        >
            {m.text}
        </span>
    );
}

// LinkedMaterial 解析到的平台材料一行: 编码 · 名称 · 类别/厚度 → 材料详情.
export function LinkedMaterial({
    material,
    source,
}: {
    material?: MaterialRef | null;
    source?: MaterialSource;
}) {
    // 类别名走 material_category 的中文名 (type1/type2 存的是 name_en 业务键).
    const catText = useCategoryText();
    if (!material) {
        return (
            <div className="flex items-center gap-2">
                <MaterialSourceBadge source={source} />
                <span className="text-slate-400">
                    {source === 'user_custom'
                        ? '本平台无此材料 (用户在 Studio 自定义/AI 保存)'
                        : '源数据未带材料标识'}
                </span>
            </div>
        );
    }
    const name = material.name?.zh || material.name?.en || material.xcode;
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <MaterialSourceBadge source={source} />
            <PermissionLink
                resource="material"
                to={`/materials/${encodeURIComponent(material.xcode)}`}
                className="font-mono text-brand-600 hover:underline"
            >
                {material.xcode}
            </PermissionLink>
            <span className="text-slate-700">{name}</span>
            <span className="text-slate-400">
                {[catText(material.type1), catText(material.type2)]
                    .filter(Boolean)
                    .join(' / ')}
                {material.thickness > 0 ? ` · ${material.thickness}mm` : ''}
            </span>
        </div>
    );
}

// SourceKindBadge 加工明细「来源」列 (对齐原型/原平台三分类): 走过识别链路 > 官方材料 > 用户自定义.
export const SOURCE_KIND_LABEL: Record<string, { text: string; cls: string }> =
    {
        recognized: { text: '材料识别', cls: 'bg-violet-50 text-violet-600' },
        official: { text: '官方材料', cls: 'bg-emerald-50 text-emerald-600' },
        user_custom: { text: '用户自定义', cls: 'bg-amber-50 text-amber-600' },
    };
export function SourceKindBadge({ kind }: { kind?: string }) {
    const m = kind ? SOURCE_KIND_LABEL[kind] : undefined;
    if (!m) return <span style={{ color: '#cbd5e1' }}>—</span>;
    return (
        <span
            className={`inline-block px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap ${m.cls}`}
        >
            {m.text}
        </span>
    );
}

/**
 * ProcessThumb 加工图缩略格 (列表「加工前」「加工后」两列各用一个).
 * 没有图时摆同尺寸灰块, 保证两列对齐.
 */
export function ProcessThumb({
    src,
    label,
    size = 44,
    onZoom,
}: {
    src?: string;
    label: string;
    size?: number;
    onZoom: (src: string) => void;
}) {
    const [broken, setBroken] = useState(false);
    // 缺图明写「无」而不是留一块灰: 源库确实有一批存档没上报结果图 (P3 机型 100% 没有),
    // 空白块会被读成"图挂了/还没加载", 看不出是"这次加工本来就没有这张图".
    // 有 URL 但渲染不出来也不能放任浏览器出裂图, 同样收成占位, 但写「图失效」——
    // 与"没有这张图"成因不同, 不能混成一句. 两类常见成因:
    //   1. 图床 404 / 测试库的 http://x/ 造数据;
    //   2. **HEIC 伪装成 .png** —— iPhone 直传的图, 源库文件名与 Content-Type 都是 image/png,
    //      实际魔数是 ftypheic, Chrome 解不了; 而且 OSS 的缩略参数对它无效 (不跳 image-res,
    //      原图 2.3MB 直接塞进 44px 格子)。线上抽样 60 条识别图命中 2 条 (3.3%),
    //      且都是 status=failed 的识别 —— 识别引擎大概也没吃下这个格式.
    if (!src || broken) {
        const isMissing = !src;
        return (
            <div title={isMissing ? `${label}:源库没有这张图` : `${label}:图片渲染不出来(地址打不开, 或格式浏览器不支持 —— iPhone 直传的 HEIC 会伪装成 .png)\n${src}`}
                className="rounded-lg border border-dashed border-slate-200 bg-slate-50 text-slate-300 text-[11px] flex items-center justify-center"
                style={{ width: size, height: size, minWidth: size }}>{isMissing ? '无' : '失效'}</div>
        );
    }
    return (
        <img src={thumbUrl(src, THUMB_W_SM)} alt={label} title={`${label} · 点击看大图`}
            className="rounded-lg object-cover ring-1 ring-slate-100 cursor-zoom-in hover:scale-105 transition-transform"
            style={{ width: size, height: size, minWidth: size }} loading="lazy"
            onError={() => setBroken(true)}
            onClick={(e) => { e.stopPropagation(); onZoom(src); }} />
    );
}

/**
 * BeforeAfterImages 加工前/后对比图 (详情页大图).
 *
 * 加工数据中心: 加工前 = 存档 archive.background_image_url (XCS 画布底图, 开工前相机拍的工作台/材料照);
 * 加工后 = 存档 image_url (complete-img). 源库约一成存档没有底图 —— 这时只摆结果图, 不占位。
 *
 * 历史加工数据 (2026-09-20) 复用同一组件, 但那边的「加工前」是视觉模型从结果图反推出来的
 * **还原图**, 不是实拍 —— 调用方用 beforeBadge 挂角标说清楚, 免得被当成真实的开工前照片。
 */
export function BeforeAfterImages({
    before,
    after,
    size,
    onZoom,
    beforeLabel = '加工前',
    afterLabel = '加工后',
    beforeBadge,
    beforeMissingText,
    afterMissingText,
}: {
    before?: string;
    after?: string;
    /** 图片最大高度 */
    size: number;
    onZoom: (src: string) => void;
    beforeLabel?: string;
    afterLabel?: string;
    /** 「加工前」标题右侧的角标 (如 AI 还原·推测) */
    beforeBadge?: React.ReactNode;
    /** 缺「加工前」图时的占位文案 (默认按"源库没有这张图"说) */
    beforeMissingText?: string;
    /** 缺「加工后」图时的占位文案 —— 历史数据要分清"本来就没上报"与"还没搬到本平台存储" */
    afterMissingText?: string;
}) {
    const [broken, setBroken] = useState<Record<string, boolean>>({});
    // 两张都摆出来, 缺的那张写「无」—— 少一张时直接不渲染的话, 看不出缺的是前还是后.
    const cells: { label: string; src?: string; badge?: React.ReactNode; missing?: string }[] = [
        { label: beforeLabel, src: before, badge: beforeBadge, missing: beforeMissingText },
        { label: afterLabel, src: after, missing: afterMissingText },
    ];

    // 上下两行而不是并排两列: 左栏只有 320px 宽, 并排后每张图窄到看不清加工细节.
    return (
        <div className="space-y-3">
            {cells.map((c) => (
                <div key={c.label} className="space-y-1">
                    <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                        <span>{c.label}</span>
                        {c.badge}
                    </div>
                    {c.src && !broken[c.label] ? (
                        <img src={thumbUrl(c.src, 640)} alt={c.label} title="点击看大图"
                            className="w-full rounded-lg object-cover cursor-zoom-in border border-slate-100"
                            style={{ maxHeight: size }}
                            onError={() => setBroken((b) => ({ ...b, [c.label]: true }))}
                            onClick={() => onZoom(c.src as string)} />
                    ) : (
                        <div className="w-full rounded-lg border border-dashed border-slate-200 bg-slate-50 text-slate-300 text-xs flex items-center justify-center"
                            style={{ height: 88 }}>
                            {c.src ? '图片地址打不开' : c.missing || '无 —— 源库没有这张图'}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

/** prism user_feedback 一条 (识别 / 矩阵两类任务同表, request_id = task_id). */
export interface FeedbackItem {
    id?: number;
    overall_rating?: string;
    problem_tags?: string[];
    detailed_feedback?: string;
    work_photo_url?: string;
    created_at?: string;
}

// FeedbackCard 任务详情左栏的「用户反馈」卡 (耗材识别 / 矩阵识别共用). 无反馈不渲染.
export function FeedbackCard({ items, onZoom }: { items: FeedbackItem[]; onZoom: (src: string) => void }) {
    if (items.length === 0) return null;
    return (
        <div className="card p-3 space-y-3">
            <div className="text-xs font-semibold text-slate-500">用户反馈</div>
            {items.map((f, i) => (
                <div key={f.id ?? i} className="text-xs text-slate-600 space-y-1.5 border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                    {f.overall_rating && <div>评价: <b>{f.overall_rating}</b></div>}
                    {(f.problem_tags?.length ?? 0) > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {f.problem_tags?.map((t) => (
                                <span key={t} className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-500 text-[11px]">{defectText(t)}</span>
                            ))}
                        </div>
                    )}
                    {f.detailed_feedback && <div className="text-slate-500">{f.detailed_feedback}</div>}
                    {f.work_photo_url && (
                        <img src={f.work_photo_url} alt="work" loading="lazy"
                            className="w-20 h-20 rounded-lg object-cover cursor-zoom-in border border-slate-100"
                            onClick={() => onZoom(f.work_photo_url || '')} />
                    )}
                </div>
            ))}
        </div>
    );
}
